import { storage } from '../storage';
import type { BillingService } from '../payments/billingService';
import { DEFAULT_SKIN_ID, findSkin, SKIN_CATALOG, type Skin } from './skinsCatalog';

const SELECTED_KEY = 'pa.skin.selected';

export interface SkinsService {
  isEnabled(): boolean;
  catalog(): Skin[];
  /** Skin ids the player may use (free skins + owned premium skins). */
  ownedIds(): Promise<string[]>;
  selectedId(): string;
  select(id: string): void;
  colorFor(skinId: string): string;
}

/**
 * Skins service. Cosmetic only. Free skins are always usable; premium skins are
 * unlocked through the billing entitlement (which is itself stubbed/dormant in
 * v1). The whole feature is hidden when FEATURES.skins is off, but colorFor()
 * still resolves the default skin so the renderer always has a color.
 */
export class StubSkinsService implements SkinsService {
  private readonly enabled: boolean;
  private readonly billing: BillingService;
  private selected: string;

  constructor(enabled: boolean, billing: BillingService) {
    this.enabled = enabled;
    this.billing = billing;
    this.selected = storage.get(SELECTED_KEY) ?? DEFAULT_SKIN_ID;
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  catalog(): Skin[] {
    return SKIN_CATALOG;
  }

  async ownedIds(): Promise<string[]> {
    const free = SKIN_CATALOG.filter((s) => !s.premium).map((s) => s.id);
    if (!this.enabled) return free;
    const { ownedSkins } = await this.billing.getEntitlements();
    return [...free, ...ownedSkins];
  }

  selectedId(): string {
    return this.selected;
  }

  select(id: string): void {
    this.selected = id;
    storage.set(SELECTED_KEY, id);
  }

  colorFor(skinId: string): string {
    return findSkin(skinId).color;
  }
}
